import { motion } from 'framer-motion';
import { Briefcase, MapPin, Clock, ArrowRight, Users, Heart, Award } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PageHeader from '@/components/common/PageHeader';

const positions = [
  {
    title: 'Immigration Consultant',
    location: 'New Delhi',
    type: 'Full-time',
    department: 'Consulting',
    description: 'Help clients navigate immigration pathways for Canada, Australia, and other countries.',
    requirements: ['3+ years immigration experience', 'ICCRC/MARA certified preferred', 'Excellent communication'],
  },
  {
    title: 'Client Relationship Manager',
    location: 'New Delhi',
    type: 'Full-time',
    department: 'Operations',
    description: 'Manage client relationships and ensure smooth processing of immigration cases.',
    requirements: ['2+ years CRM experience', 'Strong interpersonal skills', 'Multi-tasking ability'],
  },
  {
    title: 'Documentation Specialist',
    location: 'New Delhi',
    type: 'Full-time',
    department: 'Documentation',
    description: 'Prepare and review immigration documents, ensuring accuracy and compliance.',
    requirements: ['1+ years documentation experience', 'Attention to detail', 'Knowledge of immigration forms'],
  },
  {
    title: 'Digital Marketing Executive',
    location: 'Remote/New Delhi',
    type: 'Full-time',
    department: 'Marketing',
    description: 'Drive digital marketing campaigns and manage social media presence.',
    requirements: ['2+ years digital marketing', 'SEO/SEM expertise', 'Content creation skills'],
  },
];

const benefits = [
  { icon: Heart, title: 'Health Insurance', description: 'Comprehensive health coverage for you and family' },
  { icon: Award, title: 'Performance Bonus', description: 'Attractive performance-based incentives' },
  { icon: Users, title: 'Great Team', description: 'Work with passionate immigration experts' },
  { icon: Briefcase, title: 'Career Growth', description: 'Clear career progression paths' },
];

const CareersPage = () => {
  const openWhatsApp = (position) => {
    const message = `Hello Zafco, I am interested in the ${position} position. Please share more details.`;
    window.open(`https://wa.me/919958848998?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <div data-testid="careers-page">
      <PageHeader 
        title="Join Our Team"
        subtitle="Build your career with India's leading overseas recruitment consultancy"
        breadcrumbs={[{ label: 'Careers' }]}
      />

      {/* Intro */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <motion.h2 
              className="text-3xl font-bold text-zafco-blue font-montserrat mb-6"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Shape the Future of Global Mobility
            </motion.h2>
            <motion.p 
              className="text-gray-600 text-lg"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              At Zafco Human Resource Management, we're passionate about helping people achieve their 
              dreams of working abroad. Join our growing team and make a difference in people's lives.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <h3 className="text-2xl font-bold text-zafco-blue font-montserrat text-center mb-12">
            Why Work With Us
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-xl p-6 text-center shadow-sm"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="w-14 h-14 rounded-xl bg-zafco-blue/10 flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-7 h-7 text-zafco-blue" />
                </div>
                <h4 className="font-bold text-zafco-blue mb-2">{benefit.title}</h4>
                <p className="text-sm text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <h3 className="text-2xl font-bold text-zafco-blue font-montserrat text-center mb-12">
            Open Positions
          </h3>
          <div className="max-w-4xl mx-auto space-y-6">
            {positions.map((position, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100 hover:shadow-lg transition-shadow"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                  <div>
                    <h4 className="text-xl font-bold text-zafco-blue">{position.title}</h4>
                    <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" /> {position.location}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" /> {position.type}
                      </span>
                      <span className="flex items-center gap-1">
                        <Briefcase className="w-4 h-4" /> {position.department}
                      </span>
                    </div>
                  </div>
                  <Button 
                    onClick={() => openWhatsApp(position.title)}
                    className="bg-gradient-to-r from-zafco-gold to-zafco-orange text-white"
                  >
                    Apply Now <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
                <p className="text-gray-600 mb-4">{position.description}</p>
                <div className="flex flex-wrap gap-2">
                  {position.requirements.map((req, i) => (
                    <span key={i} className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">
                      {req}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-zafco-blue to-zafco-teal">
        <div className="container mx-auto px-6 text-center">
          <h3 className="text-2xl font-bold text-white mb-4">Don't See Your Role?</h3>
          <p className="text-white/80 mb-6">We're always looking for talented individuals. Send us your resume!</p>
          <Button 
            onClick={() => openWhatsApp('General Application')}
            className="bg-white text-zafco-blue hover:bg-gray-100"
          >
            Send Your Resume
          </Button>
        </div>
      </section>
    </div>
  );
};

export default CareersPage;
